import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { ITransactionLog } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-payment-transaction-log',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        ToastModule,
        FormsModule,
        DatePicker,
        ConfirmPopupModule,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './payment-transaction-log.component.html',
    styleUrl: './payment-transaction-log.component.scss',
    providers: [DatePipe]
})
export class PaymentTransactionLogComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    logs: ITransactionLog[] = [];
    loading = false;
    startDate: Date | null = null;
    endDate: Date | null = null;

    private paymentService = inject(PaymentDefinitionsService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    private datePipe = inject(DatePipe);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        const today = new Date();
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 7);
        this.startDate = weekAgo;
        this.endDate = today;
        this.searchLogs();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    searchLogs(): void {
        if (!this.startDate || !this.endDate) {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select both start and end dates.' });
            return;
        }

        this.loading = true;
        this.cd.detectChanges();
        const start = this.formatDate(this.startDate);
        const end = this.formatDate(this.endDate);

        this.paymentService.getTransactionLogs(start, end).subscribe({
            next: (res) => {
                this.logs = res.logs || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    confirmComplete(event: Event, log: ITransactionLog): void {
        this.confirmationService.confirm({
            target: event.target as HTMLElement,
            message: 'Are you sure you want to complete this reservation?',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.completeReservation(log)
        });
    }

    completeReservation(log: ITransactionLog): void {
        this.paymentService.completeReservation(log.reservationId).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Reservation has been completed successfully.'
                });
                this.searchLogs();
            }
        });
    }

    private formatDate(date: Date): string {
        return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
    }
}
